import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState, UserStateModel } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { ISPCStoreActions } from '../actions/ispc-store.actions';
import { ISPCStore } from '../models/ispc-store.model';
import { ISPCStoreService } from 'src/app/services/ispc-store.service';
import { StorageService } from 'src/app/services/storage.service';
import { DateTime } from 'luxon';

export class ISPCStoreStateModel {
    is_refreshing: boolean
    last_update: string
    ispc_store: ISPCStore[]
    user: User
}

@State<ISPCStoreStateModel>({
    name: 'ispc_store',
    defaults: {
        is_refreshing: true,
        last_update: '',
        ispc_store: [],
        user: UserStateModel.getDefaultUser()
    }
})
@Injectable()
export class ISPCStoreState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private ispc_storeService: ISPCStoreService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<ISPCStoreStateModel>) {                
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {
                        await this.reloadStateFromStorage(user)                        
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {       
        if (this.storageService.is_initialized) {
            const state_model: ISPCStoreStateModel = change.currentValue
            this.storageService.set('ispc_stores', JSON.stringify(state_model))    
        }      
    }

    async reloadStateFromStorage(user: User) : Promise<void> {        
        const json = await this.storageService.get('ispc_stores');        
        if (json) {
            const state_model: ISPCStoreStateModel = JSON.parse(json)
            if (state_model) {
                state_model.user = user
                this.store.dispatch(new ISPCStoreActions.Init(state_model));
                return
            }
        }
        this.store.dispatch(new ISPCStoreActions.Refresh(user))
    }

    async reloadStateFromApi(user: User, last_update: string) : Promise<boolean> {    
        const objs = await this.ispc_storeService.getAll(user, last_update)    
        if (objs != null) {
          const many = ISPCStore.buildMany(objs)                  
          this.store.dispatch(new ISPCStoreActions.Merge({ stores: many, user }))
          return true
        }
        return false
    }

    @Selector()
    static getLastUpdate(state: ISPCStoreStateModel) {
        return state.last_update
    }

  
    @Selector()
    static getState(state: ISPCStoreStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: ISPCStoreStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: ISPCStoreStateModel) {
        return state.ispc_store.filter(f => !f.is_deleted);
    }

    @Action(ISPCStoreActions.Refresh)
    async refresh({getState, patchState}: StateContext<ISPCStoreStateModel>, { payload }:ISPCStoreActions.Refresh) {
        const state = getState()
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi(payload, state.last_update)) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(ISPCStoreActions.Init)
    init({setState, getState}: StateContext<ISPCStoreStateModel>, { payload }:ISPCStoreActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new ISPCStoreActions.Refresh(payload.user))
    }
    
    @Action(ISPCStoreActions.Set)
    set({setState}: StateContext<ISPCStoreStateModel>, { payload }:ISPCStoreActions.Set) {             
        setState({       
            is_refreshing: false,     
            last_update: DateTime.now().toISO() as string,  
            ispc_store: payload.stores,
            user: payload.user
        })
    }

    @Action(ISPCStoreActions.Merge)
    merge({getState, setState}: StateContext<ISPCStoreStateModel>, { payload }:ISPCStoreActions.Merge) {   
        // This is to merge data from the API provided in payload, into the app state.
        // Basically, if we have server data, we trust that, so we want to replace the 
        // current app state with the payload, except:
        // 1. Any objects pending upload or delete need to be merged in, even if it no longer exists.
        // 2. Same goes for any with a service_entry that is pending approval upload.
        const state = getState()
        let oldStores = [...state.ispc_store]
        let newStores = [...payload.stores]      

        if (oldStores.length === 0) {
            oldStores = newStores
        }

        else {
            // For each store in state now...
            for (const newStore of newStores) {
                // If store id is not in the list from the server
                let oldStore = oldStores.find(f => f.id === newStore.id)
                if (oldStore) {
                    // Need to update the old store with this new one and replace it in oldStores                
                    const idx = oldStores.findIndex(f => f.id === oldStore?.id)
                    oldStores[idx] = newStore               
                } 
                else {  
                    // Else add this new store to oldStores
                    oldStores.push(newStore)
                }
            }
        }

        setState({
            is_refreshing: false,     
            last_update: DateTime.now().toISO() as string,  
            ispc_store: oldStores,
            user: payload.user
        })
    }
}
